import { Funnel, MagnifyingGlass } from 'shared/icons';

/**
 * Barra de filtros do Monitor Unificado, no padrão das abas Legacy
 * (`meli_manager/public/products-v2.html`): busca no topo → fila de atalhos de
 * um clique → painel de selects detalhados, escondido atrás do botão "Filtros".
 *
 * Por que atalhos E selects: os atalhos respondem as perguntas do dia a dia
 * ("quem está perdendo o buybox?") em um clique; o painel existe para o recorte
 * fino. Os dois escrevem no MESMO objeto de filtros — clicar em "Ganhando"
 * move o select "Status" junto, e não há dois estados a conciliar.
 *
 * Os filtros rodam no CLIENTE (a tela carrega o universo do status escolhido e
 * recorta em memória); só o status do anúncio é server-side. A BUSCA é
 * explícita: o campo é rascunho e o Enter é que aplica.
 */

const SELECT_CLASS =
    'w-full rounded-lg border border-mono-300 bg-white px-2 py-1.5 text-xs text-mono-900 outline-none focus:ring-2 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100';

const BUTTON_BASE =
    'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors';
const BUTTON_OFF =
    'border-mono-200 bg-white text-mono-600 hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400';
const BUTTON_ON = 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black';

/** Ordenação vem PRIMEIRO no painel (pedido do dono): é o que mais se mexe. */
const SORT_OPTIONS = [
    ['sales_desc', 'Vendas'],
    ['name', 'Nome'],
    ['sku', 'SKU'],
    ['category', 'Categoria'],
    ['campaign', 'Campanha'],
    ['buybox_losing', 'Prioridade: BuyBox'],
];

const AD_STATUS_OPTIONS = [
    ['active', 'Ativo'],
    ['paused', 'Pausado'],
    ['closed', 'Encerrado'],
    ['inactive', 'Inativo'],
    ['under_review', 'Em revisão'],
    ['all', 'Todos'],
];

// Status = disputa de catálogo. Enxugado a pedido do dono: os 6 valores abaixo
// e nada mais (saiu "Não listado", que dizia a mesma coisa que "Fora do
// catálogo" para quem lê a tela).
const CATALOG_STATUS_OPTIONS = [
    ['all', 'Todos'],
    ['winning', 'Ganhando'],
    ['competing', 'Perdendo'],
    ['sharing_first_place', 'Dividindo'],
    ['in_catalog', 'No catálogo'],
    ['not_in_catalog', 'Fora do catálogo'],
];

const HAS_CAMPAIGN_OPTIONS = [
    ['all', 'Todos'],
    ['yes', 'Com campanha'],
    ['no', 'Sem campanha'],
    ['better', 'Com sugestão de campanha melhor'],
];

const REBATE_OPTIONS = [
    ['', 'Todos'],
    ['with', 'Com rebate'],
    ['without', 'Sem rebate'],
];

/**
 * Estado NEUTRO das três dimensões que os atalhos controlam. Os chips são
 * EXCLUSIVOS (pedido do dono): ativar um zera os outros — por isso todo clique
 * parte deste neutro, e não de um reset por dimensão. Combinações continuam
 * possíveis pelo painel; o chip é o caminho rápido de UMA pergunta por vez.
 */
const QUICK_NEUTRAL = { hasCampaign: 'all', catalogStatus: 'all', stock: '', outdated: '' };

/**
 * Atalhos de um clique. `patch` é o pedaço de filtro que o botão aplica sobre o
 * neutro; `countKey` liga o chip à contagem facetada que o pai calcula em
 * memória: cada número diz quantos itens o chip mostraria se fosse clicado,
 * respeitando busca e painel mas ignorando o chip hoje ativo.
 */
const QUICK_FILTERS = [
    {
        id: 'with-campaign',
        label: 'Com Camp.',
        countKey: 'with_campaign',
        patch: { hasCampaign: 'yes' },
        isOn: (filters) => filters.hasCampaign === 'yes',
    },
    {
        id: 'without-campaign',
        label: 'Sem Camp.',
        countKey: 'without_campaign',
        patch: { hasCampaign: 'no' },
        isOn: (filters) => filters.hasCampaign === 'no',
    },
    {
        // Fila de trabalho: anúncios com uma campanha que passa no Pareto
        // (líquido igual ou maior E preço ao comprador igual ou menor que a
        // atual). Existia só como opção do select "Campanha" dentro do painel
        // e o dono não achava — virou chip a pedido dele (2026-09-08).
        id: 'better-campaign',
        label: 'Sugestão melhor',
        countKey: 'better_campaign',
        patch: { hasCampaign: 'better' },
        isOn: (filters) => filters.hasCampaign === 'better',
    },
    {
        id: 'winning',
        label: 'Ganhando',
        countKey: 'winning',
        patch: { catalogStatus: 'winning' },
        isOn: (filters) => filters.catalogStatus === 'winning',
    },
    {
        id: 'competing',
        label: 'Perdendo',
        countKey: 'competing',
        patch: { catalogStatus: 'competing' },
        isOn: (filters) => filters.catalogStatus === 'competing',
    },
    {
        id: 'sharing',
        label: 'Dividindo',
        countKey: 'sharing_first_place',
        patch: { catalogStatus: 'sharing_first_place' },
        isOn: (filters) => filters.catalogStatus === 'sharing_first_place',
    },
    {
        id: 'low-stock',
        label: 'Est. Baixo',
        countKey: 'low_stock',
        patch: { stock: 'low' },
        isOn: (filters) => filters.stock === 'low',
    },
    {
        // Único chip que pergunta sobre a CONFIABILIDADE do resto da tela, e não
        // sobre um atributo do anúncio: agrupa quem está com o selo vermelho
        // porque o ML recusou a última consulta.
        id: 'outdated',
        label: 'Desatualizado',
        countKey: 'outdated',
        patch: { outdated: 'yes' },
        isOn: (filters) => filters.outdated === 'yes',
    },
];

// O rótulo vai num <span> DENTRO do <label> (e não como texto solto) porque
// `uppercase`/`tracking-wide` são herdados: aplicados no <label>, chegavam nas
// <option> e a lista inteira saía em caixa alta.
const FilterSelect = ({ label, value, options, onChange }) => (
    <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-wide text-mono-400">{label}</span>
        <select value={value} onChange={(event) => onChange(event.target.value)} className={SELECT_CLASS}>
            {options.map(([optionValue, optionLabel]) => (
                <option key={optionValue} value={optionValue}>
                    {optionLabel}
                </option>
            ))}
        </select>
    </label>
);

const CampaignMonitorFilters = ({
    filters,
    options,
    quickCounts,
    searchInput,
    appliedSearch,
    onSearchChange,
    onSearchSubmit,
    onPatch,
    onReset,
    showPanel,
    onTogglePanel,
    actions,
}) => {
    // Há termo digitado que ainda não virou filtro?
    const pendingSearch = searchInput.trim() !== (appliedSearch || '');

    return (
        <div className="space-y-2.5 rounded-xl border border-mono-200 bg-white p-2.5 dark:border-mono-800 dark:bg-mono-950">
            {/* 1) BUSCA no topo, ocupando a linha — é o filtro mais usado. As ações
                (ML · Download · Atualizar) ficam à direita, na mesma linha. */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[220px] flex-1">
                    <MagnifyingGlass
                        size={14}
                        aria-hidden="true"
                        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-mono-400"
                    />
                    {/* Enter é o que APLICA a busca (`onSearchSubmit`); digitar só
                        mexe no rascunho. `preventDefault` protege do dia em que este
                        campo for parar dentro de um <form>, onde o Enter recarrega. */}
                    <input
                        type="search"
                        value={searchInput}
                        onChange={(event) => onSearchChange(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                onSearchSubmit();
                            }
                        }}
                        aria-label="Buscar anúncio por MLB, SKU ou título (Enter para buscar)"
                        placeholder="Buscar MLB, SKU ou título — Enter para buscar"
                        className={`w-full rounded-lg border bg-white py-1.5 pl-8 pr-16 text-xs text-mono-900 outline-none placeholder:text-mono-400 focus:ring-2 focus:ring-info-500 dark:bg-mono-900 dark:text-mono-100 ${
                            pendingSearch
                                ? 'border-info-500 dark:border-info-500'
                                : 'border-mono-300 dark:border-mono-700'
                        }`}
                    />
                    {/* Rascunho ≠ filtro aplicado: sem este aviso o campo mostra um
                        termo que a lista abaixo não está usando, e ninguém descobre
                        que falta apertar Enter. Fica em `right-7` para não montar
                        em cima do X nativo do `type="search"`, que aparece
                        exatamente quando há texto — ou seja, sempre que este
                        aviso está visível. */}
                    {pendingSearch ? (
                        <span className="pointer-events-none absolute right-7 top-1/2 -translate-y-1/2 text-[10px] font-medium text-info-600 dark:text-info-400">
                            Enter ↵
                        </span>
                    ) : null}
                </div>
                {actions}
            </div>

            {/* 2) FILA DE ATALHOS. "Filtros" vem PRIMEIRO (pedido do dono) porque é o
                que revela o painel — os demais já aplicam um recorte direto. */}
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={onTogglePanel}
                    aria-expanded={showPanel}
                    className={`${BUTTON_BASE} ${showPanel ? BUTTON_ON : BUTTON_OFF}`}
                >
                    <Funnel size={13} aria-hidden="true" />
                    Filtros
                </button>
                {QUICK_FILTERS.map((quick) => {
                    const active = quick.isOn(filters);
                    const count = quickCounts?.[quick.countKey];
                    return (
                        <button
                            key={quick.id}
                            type="button"
                            // Exclusivos: todo clique parte do NEUTRO — ativar um chip
                            // desliga qualquer outro; clicar no aceso volta ao neutro.
                            onClick={() => onPatch(active ? { ...QUICK_NEUTRAL } : { ...QUICK_NEUTRAL, ...quick.patch })}
                            aria-pressed={active}
                            className={`${BUTTON_BASE} ${active ? BUTTON_ON : BUTTON_OFF}`}
                        >
                            {quick.label}
                            {/* Contagem facetada (respeita busca + painel). `null` =
                                endpoint falhou/carregando: o chip funciona sem número,
                                nunca mostra 0 falso. */}
                            {typeof count === 'number' ? (
                                <span className={`tabular-nums ${active ? 'opacity-80' : 'text-mono-400'}`}>
                                    {count}
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {/* 3) PAINEL detalhado, na ordem pedida pelo dono. */}
            {showPanel ? (
                <div className="border-t border-mono-100 pt-2.5 dark:border-mono-900">
                    <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-5">
                        <FilterSelect
                            label="Ordenar por"
                            value={filters.sortBy}
                            options={SORT_OPTIONS}
                            onChange={(value) => onPatch({ sortBy: value })}
                        />
                        <FilterSelect
                            label="Anúncio"
                            value={filters.status}
                            options={AD_STATUS_OPTIONS}
                            onChange={(value) => onPatch({ status: value })}
                        />
                        <FilterSelect
                            label="Status"
                            value={filters.catalogStatus}
                            options={CATALOG_STATUS_OPTIONS}
                            onChange={(value) => onPatch({ catalogStatus: value })}
                        />
                        <FilterSelect
                            label="Campanha"
                            value={filters.hasCampaign}
                            options={HAS_CAMPAIGN_OPTIONS}
                            onChange={(value) => onPatch({ hasCampaign: value })}
                        />
                        <FilterSelect
                            label="Rebate"
                            value={filters.rebate}
                            options={REBATE_OPTIONS}
                            onChange={(value) => onPatch({ rebate: value })}
                        />
                        <FilterSelect
                            label="Marca"
                            value={filters.brand}
                            options={[['', 'Todas'], ...options.brands.map((brand) => [brand, brand])]}
                            onChange={(value) => onPatch({ brand: value })}
                        />
                        {/* Categoria vem do backend já traduzida:
                            `{ id: "MLB1077", name: "Ração para Cães" }`, com o nome
                            resolvido em `/categories/:id` e espelhado em
                            `ml_categories`. Quando o ML não resolve, `name` é o
                            próprio código — o rótulo degrada, nunca é inventado. */}
                        <FilterSelect
                            label="Categoria"
                            value={filters.categoryId}
                            options={[
                                ['', 'Todas'],
                                ...options.categories.map((category) => [category.id, category.name || category.id]),
                            ]}
                            onChange={(value) => onPatch({ categoryId: value })}
                        />
                        <FilterSelect
                            label="Tipo Campanha"
                            value={filters.campaignType}
                            options={[['', 'Todos'], ...options.campaign_types.map((type) => [type, type])]}
                            onChange={(value) => onPatch({ campaignType: value })}
                        />
                        <FilterSelect
                            label="Campanha"
                            value={filters.campaignId}
                            options={[
                                ['', 'Todas'],
                                ...options.campaigns.map((campaign) => [campaign.id, campaign.name]),
                            ]}
                            onChange={(value) => onPatch({ campaignId: value })}
                        />
                        <FilterSelect
                            label="Estoque"
                            value={filters.stock}
                            options={[
                                ['', 'Todos'],
                                ['with', 'Com estoque'],
                                ['without', 'Sem estoque'],
                                ['low', 'Baixo (<10)'],
                            ]}
                            onChange={(value) => onPatch({ stock: value })}
                        />
                    </div>
                    <div className="mt-2.5 flex justify-end">
                        <button
                            type="button"
                            onClick={onReset}
                            className="rounded-lg border border-mono-200 px-3 py-1.5 text-xs text-mono-600 transition-colors hover:border-mono-400 dark:border-mono-800 dark:text-mono-400"
                        >
                            Limpar
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CampaignMonitorFilters;
